import { useEffect } from 'react';
import { useMatch } from 'react-router-dom';

import { OrdersModel, ordersApi } from '@pukka/api';
import { Button, Text } from '@pukka/ui-components';
import { useQuery } from '@tanstack/react-query';

import ReceiptBusinessInfo from './ReceiptBusinessInfo';
import ReceiptCountingBox from './ReceiptCountingBox';
import ReceiptInfo from './ReceiptInfo';
import { ReceiptListItem } from './ReceiptListItem';
import ReceiptLoyaltyInfo from './ReceiptLoyaltyInfo';
import ReceiptTotal from './ReceiptTotal';

interface InvoiceStatusProps {
  readonly status: string;
  readonly text: string;
}

interface ReceiptSummaryProps {
  readonly invoiceDetail: OrdersModel.OrderDetail;
}

interface ReceiptMainContentProps {
  readonly invoiceDetail: OrdersModel.OrderDetail;
}

function InvoiceStatus({ status, text }: InvoiceStatusProps) {
  const styles = 'rounded-full border py-2 px-3 text-sm text-white border-none';

  let bgColor = '';

  if (status === 'paid') {
    bgColor = 'bg-green-500';
  } else if (status === 'cancelled' || text.includes('Overdue')) {
    bgColor = 'bg-red-500';
  } else if (status === 'partially_paid') {
    bgColor = 'bg-yellow-500';
  } else {
    // text: due in today, due in x days, and other unknown statuses
    bgColor = 'bg-neutral-500';
  }

  return <div className={`${styles} ${bgColor}`}>{text}</div>;
}

function ReceiptSummary({ invoiceDetail }: ReceiptSummaryProps) {
  return (
    <div>
      <Text color="blue" weight="semibold" variant="title-1" className="py-3">
        Summary
      </Text>
      <ReceiptListItem invoiceDetail={invoiceDetail} />
      <hr />
      <div className="mt-2 flex justify-end">
        <div className="w-full rounded-lg bg-neutral-50 sm:w-[50%]">
          <ReceiptCountingBox invoiceDetail={invoiceDetail} />
          <hr />
          <ReceiptTotal invoiceDetail={invoiceDetail} />
        </div>
      </div>
    </div>
  );
}

function ReceiptMainContent({ invoiceDetail }: ReceiptMainContentProps) {
  return (
    <>
      {invoiceDetail.order_refunds.map((invoiceRefund, idx) => {
        return (
          <div key={idx}>
            <ReceiptInfo invoiceDetail={invoiceDetail} invoiceRefund={invoiceRefund} />
            <ReceiptSummary invoiceDetail={invoiceDetail} />
            <hr className="my-4" />
            <ReceiptLoyaltyInfo
              loyalty={invoiceDetail.loyalty}
              memberPhone={invoiceDetail.customer_phone}
              totalAmountDue={invoiceDetail.total_amount_due}
            />
          </div>
        );
      })}

      {invoiceDetail.order_refunds.length === 0 && (
        <div>
          <ReceiptInfo invoiceDetail={invoiceDetail} />
          <ReceiptSummary invoiceDetail={invoiceDetail} />
          <hr className="my-4" />
          <ReceiptLoyaltyInfo
            loyalty={invoiceDetail.loyalty}
            memberPhone={invoiceDetail.customer_phone}
            totalAmountDue={invoiceDetail.total_amount_due}
          />
        </div>
      )}
    </>
  );
}

function InvoiceReceiptPage() {
  const match = useMatch('v2/reports/invoices/print/:token');
  const token = match?.params.token;

  const { data } = useQuery({
    queryKey: ['invoice-detail'],
    queryFn: () => ordersApi.getOrderDetail(token ?? ''),
    enabled: !!token,
  });

  const invoiceDetail = data;

  useEffect(() => {
    if (invoiceDetail) {
      document.title = `Payment Invoice #${invoiceDetail.invoice_no} is ${invoiceDetail.status_description}`;
    }
  }, [invoiceDetail]);

  // hide onetrust floating button from print preview
  useEffect(() => {
    const floatingBtn = document.getElementById('ot-sdk-btn-floating');

    /* istanbul ignore next */
    if (floatingBtn) {
      floatingBtn.style.display = 'none';
    }
  }, []);

  return invoiceDetail ? (
    <div className="w-full overflow-auto">
      <div className="sticky top-0 z-60 flex justify-end bg-white px-4 py-2 shadow-md print:hidden">
        <Button size="m" onClick={() => window.print()}>
          Print
        </Button>
      </div>
      <div
        style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
        className="m-auto max-w-[800px] px-5 py-[10px]"
        id="receipt"
      >
        <ReceiptBusinessInfo
          businessName={invoiceDetail.business_name}
          outletName={invoiceDetail.outlet_name}
          outletAddress={invoiceDetail.outlet_address}
          outletPhone={invoiceDetail.outlet_phone}
        />

        <hr />

        <div className="mb-2 mt-4 flex gap-3">
          <Text color="blue" weight="semibold" variant="title-1" className="content-center">
            Invoice
          </Text>
          <InvoiceStatus status={invoiceDetail.invoice_status} text={invoiceDetail.status_description} />
        </div>

        <ReceiptMainContent invoiceDetail={invoiceDetail} />
      </div>
    </div>
  ) : null;
}

export { InvoiceReceiptPage as default, InvoiceStatus, ReceiptMainContent };
